import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Space,
  Table,
  Typography,
  Switch,
  Select,
} from "antd";
import dayjs from "dayjs";
import React, { useState } from "react";
import UserStatusTag, { status } from "../shared/UserStatusTag";
import { formatDateTime } from "../../helpers/date";

const { TextArea } = Input;
const { Text } = Typography;
const TOPUP_PACKAGES = [20, 50, 100, 500, 1000, 2000, 3000, 5000];

const renderCampaignTable = (fieldPathPrefix) => (
  <Table
    bordered
    pagination={false}
    rowKey="packageAmount"
    dataSource={TOPUP_PACKAGES.map((amount, index) => ({
      packageAmount: amount,
      rowIndex: index,
    }))}
    columns={[
      {
        title: "Package (RM)",
        dataIndex: "packageAmount",
        render: (_, record) => (
          <>
            <Form.Item
              name={[...fieldPathPrefix, record.rowIndex, "packageAmount"]}
              initialValue={record.packageAmount}
              hidden
            >
              <Input />
            </Form.Item>
            <span>{`RM ${record.packageAmount}`}</span>
          </>
        ),
      },
      {
        title: "Bonus (RM)",
        render: (_, record) => (
          <Form.Item
            name={[...fieldPathPrefix, record.rowIndex, "bonus"]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              min={0}
              precision={2}
              placeholder="Optional bonus amount"
              style={{ width: "100%" }}
            />
          </Form.Item>
        ),
      },
      {
        title: "Limit (Optional)",
        render: (_, record) => (
          <Form.Item
            name={[...fieldPathPrefix, record.rowIndex, "limit"]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              min={1}
              placeholder="No limit"
              style={{ width: "100%" }}
            />
          </Form.Item>
        ),
      },
    ]}
  />
);

const renderEditTable = (fieldPathPrefix) => (
  <Table
    bordered
    pagination={false}
    rowKey="packageAmount"
    dataSource={TOPUP_PACKAGES.map((amount, index) => ({
      packageAmount: amount,
      rowIndex: index,
    }))}
    size="small"
    columns={[
      {
        title: "Package (RM)",
        dataIndex: "packageAmount",
        render: (_, record) => (
          <>
            <Form.Item
              name={[...fieldPathPrefix, record.rowIndex, "packageAmount"]}
              initialValue={record.packageAmount}
              hidden
            >
              <Input />
            </Form.Item>
            <span>RM {record.packageAmount}</span>
          </>
        ),
      },
      {
        title: "Bonus (RM)",
        render: (_, record) => (
          <Form.Item
            name={[...fieldPathPrefix, record.rowIndex, "bonus"]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              min={0}
              precision={2}
              placeholder="Optional bonus amount"
              style={{ width: "100%" }}
            />
          </Form.Item>
        ),
      },
      {
        title: "Limit",
        render: (_, record) => (
          <Form.Item
            name={[...fieldPathPrefix, record.rowIndex, "limit"]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              min={1}
              placeholder="No limit"
              style={{ width: "100%" }}
            />
          </Form.Item>
        ),
      },
    ]}
  />
);

const BonusCampaignForm = ({
  form,
  loading,
  mode = "create",
  onSubmit,
  onCancel,
  submitText,
  selectedCampaign = {},
}) => {
  const isEditMode = mode !== "create";
  const [campaignDetailsError, setCampaignDetailsError] = useState("");

  const hasAtLeastOneValidBonus = (campaignDetails = []) => {
    return campaignDetails.some((item) => {
      const bonus = item?.bonus;
      return (
        bonus !== undefined &&
        bonus !== null &&
        bonus !== "" &&
        Number(bonus) > 0
      );
    });
  };

  const handleFinish = (values) => {
    const campaignDetails = values.campaignDetails || [];

    if (!hasAtLeastOneValidBonus(campaignDetails)) {
      setCampaignDetailsError(
        "At least one bonus amount greater than 0 is required.",
      );
      form.scrollToField(["campaignDetails", 0, "bonus"]);
      return;
    }

    setCampaignDetailsError("");
    onSubmit(values);
  };

  const handleValuesChange = (_, allValues) => {
    const campaignDetails = allValues.campaignDetails || [];

    if (hasAtLeastOneValidBonus(campaignDetails)) {
      setCampaignDetailsError("");
    } else if (campaignDetailsError) {
      setCampaignDetailsError(
        "At least one bonus amount greater than 0 is required.",
      );
    }
  };

  return (
    <Card
      bordered={false}
      style={{
        borderRadius: 16,
        boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
        background: "#fcfcfd",
      }}
    >
      <Form
        form={form}
        name="new-bonus-campaign-form"
        layout="vertical"
        autoComplete="off"
        initialValues={{ category: "all" }}
        onFinish={handleFinish}
        onValuesChange={handleValuesChange}
      >
        <div style={{ marginBottom: 20 }}>
          <Typography.Title level={4} style={{ marginBottom: 4 }}>
            {isEditMode ? "Edit Bonus Campaign" : "Create New Bonus Campaign"}
          </Typography.Title>
          <Text type="secondary">
            Campaigns are created <strong>inactive</strong> by default. Activate
            them from the list after creation.
          </Text>
        </div>

        <Card
          size="small"
          bordered={false}
          style={{
            marginBottom: 20,
            background: "#fff",
            borderRadius: 12,
          }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24}>
              <Form.Item
                label="Target"
                name="category"
                rules={[{ required: true }]}
              >
                {isEditMode ? (
                  <div
                    style={{
                      padding: "10px 14px",
                      borderRadius: 8,
                      background: "#e6f4ff",
                      border: "1px solid #91caff",
                      fontWeight: 500,
                    }}
                  >
                    {selectedCampaign?.category === "userStatus"
                      ? "By User Status"
                      : "All Users"}
                  </div>
                ) : (
                  <Radio.Group
                    options={[
                      { label: "All Users", value: "all" },
                      { label: "By User Status", value: "userStatus" },
                    ]}
                    optionType="button"
                    buttonStyle="solid"
                    onChange={() => {
                      const category = form.getFieldValue("category");
                      form.resetFields();
                      form.setFieldsValue({ category });
                      setCampaignDetailsError("");
                    }}
                  />
                )}
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Campaign Name"
                name="name"
                rules={[
                  { required: true, message: "Please enter campaign name" },
                  { max: 50, message: "Max 50 characters" },
                ]}
              >
                <Input placeholder="e.g. Raya Bonus Campaign" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Campaign Period (cannot be changed)"
                name="campaignPeriod"
                rules={[
                  {
                    required: !isEditMode,
                    message: "Please select campaign period",
                  },
                ]}
              >
                {selectedCampaign.startDate && selectedCampaign.endDate ? (
                  <div>
                    <Text>
                      {selectedCampaign.startDate
                        ? formatDateTime(selectedCampaign.startDate).substring(
                            0,
                            10,
                          )
                        : "-"}{" "}
                      →{" "}
                      {selectedCampaign.endDate
                        ? formatDateTime(selectedCampaign.endDate).substring(
                            0,
                            10,
                          )
                        : "-"}
                    </Text>
                  </div>
                ) : (
                  <DatePicker.RangePicker
                    disabled={isEditMode}
                    style={{ width: "100%" }}
                    format="YYYY-MM-DD"
                    placeholder={["Start Date", "End Date"]}
                    disabledDate={(current) =>
                      current && current < dayjs().startOf("day")
                    }
                  />
                )}
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item label="Description" name="description">
                <TextArea rows={2} placeholder="Optional short description" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Exclude Users With Previous Topup"
                name="isFirstTopupOnly"
                valuePropName="checked"
                initialValue={selectedCampaign?.isFirstTopupOnly || false}
                tooltip="Users with prior topup history will be excluded from this campaign."
              >
                <Switch />
              </Form.Item>
            </Col>

            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev.category !== curr.category || prev.status !== curr.status
              }
            >
              {({ getFieldValue }) => {
                const category =
                  getFieldValue("category") || selectedCampaign?.category;

                if (category !== "userStatus") return null;

                const selectedStatusKey =
                  getFieldValue("status") || selectedCampaign?.status;

                const selectedStatus = status.find(
                  (item) => item.key === selectedStatusKey,
                );

                return (
                  <Col xs={24} md={12}>
                    <Form.Item label="User Status" name="status">
                      {isEditMode ? (
                        <div
                          style={{
                            padding: "10px 14px",
                            borderRadius: 8,
                            background: "#e6f4ff",
                            border: "1px solid #91caff",
                          }}
                        >
                          {selectedStatus ? (
                            <>
                              <div style={{ fontWeight: 500 }}>
                                <UserStatusTag statusKey={selectedStatus.name}>
                                  {selectedStatus.name}
                                </UserStatusTag>
                              </div>
                              <div style={{ fontSize: 12, color: "#666" }}>
                                {selectedStatus.title}
                              </div>
                            </>
                          ) : (
                            "-"
                          )}
                        </div>
                      ) : (
                        <Select placeholder="Select user status">
                          {status.map((item) => (
                            <Select.Option key={item.key} value={item.key}>
                              <UserStatusTag statusKey={item.name}>
                                {item.name}
                              </UserStatusTag>
                            </Select.Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                  </Col>
                );
              }}
            </Form.Item>
          </Row>
        </Card>

        <Card
          size="small"
          title="Reward Badge Label (shown on dashboard)"
          bordered={false}
          style={{
            marginBottom: 20,
            background: "#fff",
            borderRadius: 12,
          }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} md={8}>
              <Form.Item
                label="English"
                name="badge_en"
                rules={[
                  {
                    required: true,
                    message: "Please enter reward badge label in English",
                  },
                  {
                    whitespace: true,
                    message: "Reward badge label in English cannot be empty",
                  },
                  {
                    max: 15,
                    message: "Maximum 15 characters allowed",
                  },
                ]}
              >
                <TextArea
                  rows={2}
                  maxLength={15}
                  placeholder="e.g. FREE Credits"
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                label="Chinese"
                name="badge_zh"
                rules={[
                  {
                    required: true,
                    message: "Please enter reward badge label in Chinese",
                  },
                  {
                    whitespace: true,
                    message: "Reward badge label in Chinese cannot be empty",
                  },
                  {
                    max: 15,
                    message: "Maximum 15 characters allowed",
                  },
                ]}
              >
                <TextArea rows={2} maxLength={15} placeholder="e.g. 免费积分" />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                label="Malay"
                name="badge_ms"
                rules={[
                  {
                    required: true,
                    message: "Please enter reward badge label in Malay",
                  },
                  {
                    whitespace: true,
                    message: "Reward badge label in Malay cannot be empty",
                  },
                  {
                    max: 15,
                    message: "Maximum 15 characters allowed",
                  },
                ]}
              >
                <TextArea
                  rows={2}
                  maxLength={15}
                  placeholder="e.g. Kredit Percuma"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          size="small"
          title="Bonus per Package"
          bordered={false}
          style={{
            marginBottom: 20,
            background: "#fff",
            borderRadius: 12,
          }}
        >
          <div
            style={{
              marginBottom: 12,
              padding: "10px 14px",
              background: "#fafafa",
              border: "1px solid #f0f0f0",
              borderRadius: 8,
            }}
          >
            <Text type="secondary">
              Set the bonus and optional limit per package.
            </Text>
          </div>

          {isEditMode
            ? renderEditTable(["campaignDetails"])
            : renderCampaignTable(["campaignDetails"])}

          {!!campaignDetailsError && (
            <div
              style={{
                color: "#ff4d4f",
                fontSize: 12,
                marginTop: 8,
              }}
            >
              {campaignDetailsError}
            </div>
          )}
        </Card>

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {submitText ||
                (isEditMode ? "Update Campaign" : "Create Campaign")}
            </Button>
          </Space>
        </div>
      </Form>
    </Card>
  );
};

export default BonusCampaignForm;
